import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTheme, useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format, differenceInDays, addDays } from "date-fns";
import { it } from "date-fns/locale";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  Plane, 
  MapPin, 
  Calendar, 
  Plus, 
  Sun, 
  Moon, 
  ArrowLeft,
  Clock,
  Trash2,
  Loader2,
  Menu,
  X,
  LogOut
} from "lucide-react";
import AIAssistant from "../components/AIAssistant";
import GeocodingInput from "../components/GeocodingInput";

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
});

const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
};

const TripDetail = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { token, logout } = useAuth();
  const [trip, setTrip] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [mapCenter, setMapCenter] = useState([41.9028, 12.4964]); // Rome default
  const [newItem, setNewItem] = useState({
    title: "",
    description: "",
    time: "",
    location: "",
    latitude: null,
    longitude: null,
    type: "activity"
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tripRes, itineraryRes] = await Promise.all([
        axios.get(`${API}/trips/${tripId}`, { headers }),
        axios.get(`${API}/itinerary/${tripId}`, { headers })
      ]);
      setTrip(tripRes.data);
      setItinerary(itineraryRes.data);
      
      // Set map center if trip has coordinates
      if (tripRes.data.latitude && tripRes.data.longitude) {
        setMapCenter([tripRes.data.latitude, tripRes.data.longitude]);
      }
    } catch (error) {
      toast.error("Errore nel caricamento del viaggio");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && tripId) fetchData();
  }, [token, tripId]);

  const handleAddItem = async () => {
    if (!newItem.title) {
      toast.error("Inserisci un titolo");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/itinerary`, {
        ...newItem,
        trip_id: tripId,
        day: selectedDay
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setItinerary([...itinerary, response.data].sort((a, b) => {
        if (a.day !== b.day) return a.day - b.day;
        return (a.time || "").localeCompare(b.time || "");
      }));
      setAddItemOpen(false);
      setNewItem({ title: "", description: "", time: "", location: "", latitude: null, longitude: null, type: "activity" });
      toast.success("Attività aggiunta!");
    } catch (error) {
      toast.error("Errore nell'aggiunta dell'attività");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/itinerary/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItinerary(itinerary.filter(i => i.item_id !== itemId));
      toast.success("Attività eliminata");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteTrip = async () => {
    try {
      await axios.delete(`${API}/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Viaggio eliminato");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Errore nell'eliminazione del viaggio");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!trip) return null;

  const tripDays = differenceInDays(new Date(trip.end_date), new Date(trip.start_date)) + 1;
  const days = Array.from({ length: tripDays }, (_, i) => i + 1);
  const itemsForDay = itinerary.filter(item => item.day === selectedDay);
  const markersForDay = itemsForDay.filter(item => item.latitude && item.longitude);

  const getItemTypeIcon = (type) => {
    switch(type) {
      case "food": return "🍽️";
      case "transport": return "🚗";
      case "accommodation": return "🏨";
      default: return "📍";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <Plane className="w-6 h-6 text-primary" />
                <span className="font-heading text-lg font-bold hidden sm:inline">TravelPro</span>
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Dashboard
              </Link>
              <Link to="/bookings" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Prenotazioni
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="trip-theme-toggle">
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="hidden md:flex" data-testid="trip-logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 py-4 space-y-2">
            <Link to="/dashboard" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Dashboard
            </Link>
            <Link to="/bookings" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Prenotazioni
            </Link>
            <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trip Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2" data-testid="trip-title">
              {trip.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {trip.destination}
              </span>
              <span className="flex items-center gap-1 font-mono text-sm">
                <Calendar className="w-4 h-4" />
                {format(new Date(trip.start_date), "dd MMM", { locale: it })} - {format(new Date(trip.end_date), "dd MMM yyyy", { locale: it })}
              </span>
              <span className="text-sm">({tripDays} giorni)</span>
            </div>
            {trip.description && (
              <p className="mt-2 text-muted-foreground">{trip.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="delete-trip-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare questo viaggio?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Questa azione non può essere annullata. Verranno eliminati anche tutti gli itinerari associati.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteTrip} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Elimina
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Map */}
          <Card className="border-border overflow-hidden">
            <CardContent className="p-0">
              <div className="h-[400px] rounded-xl overflow-hidden">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  scrollWheelZoom={true}
                  className="h-full w-full z-0"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapController center={mapCenter} />
                  {markersForDay.map((item, idx) => (
                    <Marker 
                      key={item.item_id} 
                      position={[item.latitude, item.longitude]}
                    >
                      <Popup>
                        <div className="font-semibold">{item.title}</div>
                        {item.time && <div className="text-sm text-gray-600">{item.time}</div>}
                        {item.location && <div className="text-sm">{item.location}</div>}
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Itinerary */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg">Itinerario</CardTitle>
              <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-full" data-testid="add-itinerary-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Aggiungi attività - Giorno {selectedDay}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="item-title">Titolo *</Label>
                      <Input 
                        id="item-title"
                        placeholder="Es. Visita al Colosseo"
                        value={newItem.title}
                        onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                        data-testid="item-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="item-time">Orario</Label>
                        <Input 
                          id="item-time"
                          type="time"
                          value={newItem.time}
                          onChange={(e) => setNewItem({...newItem, time: e.target.value})}
                          data-testid="item-time-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="item-type">Tipo</Label>
                        <Select value={newItem.type} onValueChange={(v) => setNewItem({...newItem, type: v})}>
                          <SelectTrigger data-testid="item-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activity">Attività</SelectItem>
                            <SelectItem value="food">Ristorante</SelectItem>
                            <SelectItem value="transport">Trasporto</SelectItem>
                            <SelectItem value="accommodation">Alloggio</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="item-location">Luogo (con ricerca automatica)</Label>
                      <GeocodingInput
                        value={newItem.location}
                        onChange={(value) => setNewItem({...newItem, location: value})}
                        onLocationSelect={(lat, lng, displayName) => {
                          setNewItem({
                            ...newItem, 
                            location: displayName.split(",")[0],
                            latitude: lat, 
                            longitude: lng
                          });
                          // Update map center
                          setMapCenter([lat, lng]);
                        }}
                        placeholder="Cerca un luogo..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="item-lat">Latitudine</Label>
                        <Input 
                          id="item-lat"
                          type="number"
                          step="any"
                          placeholder="Rilevata automaticamente"
                          value={newItem.latitude || ""}
                          onChange={(e) => setNewItem({...newItem, latitude: parseFloat(e.target.value) || null})}
                          data-testid="item-lat-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="item-lng">Longitudine</Label>
                        <Input 
                          id="item-lng"
                          type="number"
                          step="any"
                          placeholder="Rilevata automaticamente"
                          value={newItem.longitude || ""}
                          onChange={(e) => setNewItem({...newItem, longitude: parseFloat(e.target.value) || null})}
                          data-testid="item-lng-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="item-desc">Note</Label>
                      <Textarea 
                        id="item-desc"
                        placeholder="Aggiungi delle note..."
                        value={newItem.description}
                        onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                        data-testid="item-desc-input"
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={handleAddItem}
                      disabled={saving}
                      data-testid="item-submit-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Aggiungi
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {/* Day selector */}
              <div className="flex gap-2 overflow-x-auto pb-4 mb-4 border-b border-border">
                {days.map((day) => (
                  <Button
                    key={day}
                    variant={selectedDay === day ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedDay(day)}
                    className="shrink-0"
                    data-testid={`day-${day}-btn`}
                  >
                    <span className="font-mono">G{day}</span>
                    <span className="ml-1 text-xs hidden sm:inline">
                      {format(addDays(new Date(trip.start_date), day - 1), "dd/MM", { locale: it })}
                    </span>
                  </Button>
                ))}
              </div>

              {/* Timeline */}
              {itemsForDay.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Nessuna attività per questo giorno</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {itemsForDay.map((item, idx) => (
                    <div 
                      key={item.item_id}
                      className="flex gap-4 group"
                      data-testid={`itinerary-item-${item.item_id}`}
                    >
                      {/* Timeline line */}
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg shrink-0">
                          {getItemTypeIcon(item.type)}
                        </div>
                        {idx < itemsForDay.length - 1 && (
                          <div className="w-0.5 flex-1 bg-border mt-2" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{item.title}</h4>
                            {item.time && (
                              <p className="text-sm text-muted-foreground font-mono">{item.time}</p>
                            )}
                            {item.location && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {item.location}
                              </p>
                            )}
                            {item.description && (
                              <p className="text-sm text-muted-foreground mt-2">{item.description}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                            onClick={() => handleDeleteItem(item.item_id)}
                            data-testid={`delete-item-${item.item_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <AIAssistant />
    </div>
  );
};

export default TripDetail;
